// Public Roadmap page — shipped / in progress / planned features
import { motion } from "framer-motion";
import { Check, GitBranch, Rocket } from "lucide-react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

const ROADMAP = [
  {
    status: "shipped",
    quarter: "T1 2026",
    items: [
      { title: "Génération de workflows par IA", desc: "Claude Sonnet 4.5 traduit le langage naturel en workflow exécutable." },
      { title: "Éditeur no-code", desc: "Formulaire guidé avec drag-to-reorder visuel." },
      { title: "Analytics ROI", desc: "Calcul automatique des heures économisées et du coût évité." },
      { title: "Stripe Checkout", desc: "Paiement des plans Starter, Pro, Business en €." },
      { title: "Templates marketplace", desc: "10 workflows pré-construits installables en 1 clic." },
      { title: "AI Copilot", desc: "Assistant flottant pour aider à utiliser l'app." },
    ],
  },
  {
    status: "in_progress",
    quarter: "T2 2026",
    items: [
      { title: "OAuth réel Gmail / Slack / Notion", desc: "Connexions vraies, plus de simulation." },
      { title: "Exécution réelle des workflows", desc: "Moteur d'exécution distribué avec retry automatique." },
      { title: "SOC 2 Type II", desc: "Certification de sécurité enterprise." },
      { title: "Multi-workspace", desc: "Collaboration en équipe avec rôles RBAC." },
    ],
  },
  {
    status: "planned",
    quarter: "T3 2026",
    items: [
      { title: "Drag & drop canvas visuel", desc: "Éditeur graphique style React Flow avec connexions libres." },
      { title: "Workflows conditionnels avancés", desc: "Branchements multi-niveaux, boucles, parallèle." },
      { title: "Intégrations HubSpot / Salesforce / Linear / Outlook", desc: "Extension du catalogue." },
      { title: "SSO SAML pour entreprises", desc: "Single Sign-On Okta, Azure AD, Google Workspace." },
    ],
  },
  {
    status: "planned",
    quarter: "T4 2026",
    items: [
      { title: "Multi-agent orchestration", desc: "Plusieurs agents IA collaborent sur un même workflow complexe." },
      { title: "Self-healing workflows", desc: "Détection automatique d'erreurs + correction IA." },
      { title: "Marketplace communautaire", desc: "Partage de templates entre utilisateurs." },
      { title: "Mobile app native", desc: "iOS + Android pour monitorer et déclencher des workflows." },
    ],
  },
];

const STATUS_META = {
  shipped: { label: "LIVRÉ", color: "bg-emerald-100 text-emerald-700", icon: Check, dot: "bg-emerald-500" },
  in_progress: { label: "EN COURS", color: "bg-blue-100 text-blue-700", icon: GitBranch, dot: "bg-blue-500" },
  planned: { label: "PLANIFIÉ", color: "bg-zinc-100 text-zinc-700", icon: Rocket, dot: "bg-zinc-400" },
};

export default function Roadmap() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicHeader />
      <section className="max-w-5xl mx-auto px-6 py-20" data-testid="roadmap-page">
        <div className="mb-16">
          <span className="overline">Transparence radicale</span>
          <h1 className="font-display text-5xl lg:text-6xl font-medium tracking-tighter mt-4 mb-6 max-w-3xl">
            Roadmap publique
          </h1>
          <p className="text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Voici ce qu'on a livré, ce qu'on construit en ce moment, et ce qu'on prévoit.
            On publie tout — parce que vous méritez de savoir où va le produit que vous payez.
          </p>
        </div>

        <div className="space-y-12">
          {ROADMAP.map((section, idx) => {
            const meta = STATUS_META[section.status];
            const Icon = meta.icon;
            return (
              <motion.div
                key={section.quarter}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.08 }}
                className="relative"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className={`w-2 h-2 rounded-full ${meta.dot}`}/>
                  <span className="font-display text-2xl font-medium tracking-tight">{section.quarter}</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${meta.color}`}>{meta.label}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-5 border-l border-zinc-200 pl-6">
                  {section.items.map((item, i) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06 }}
                      className="bg-white border border-zinc-200 rounded-sm p-5"
                    >
                      <div className="flex items-start gap-3">
                        <Icon size={16} strokeWidth={1.5} className="mt-0.5 text-zinc-600 shrink-0"/>
                        <div>
                          <h3 className="font-display font-medium tracking-tight mb-1">{item.title}</h3>
                          <p className="text-sm text-zinc-600 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-20 p-10 border border-zinc-200 rounded-sm bg-white">
          <h2 className="font-display text-2xl font-medium tracking-tight mb-2">Une idée pour la roadmap ?</h2>
          <p className="text-zinc-600 mb-4">Écrivez-nous à <a href="mailto:product@flowmindai.eu" className="klein-text hover:underline">product@flowmindai.eu</a>. Les meilleures idées des clients deviennent souvent des features prioritaires.</p>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
